/* eslint-disable max-lines */
import * as moment from "moment";
import { createValidator } from "not-valid";

import { QuestionnaireData } from "../questionnaires/model";
import { ReportDetails } from "../reportsCommon/reportsCommon";

// This follows the versioning of ClinicalNotesVersion, should be able to merge them into one at some point
export const ReferralReportVersion = {
    V1: 1,

    V2: 2,

    V3: 3,

    Latest: 4
} as const ;

export enum Classification {
    AtWorkMedicalCondition = "AtWorkMedicalCondition",
    AtWorkManagementIssue = "AtWorkManagementIssue",
    NotAtWorkMedicalCondition = "NotAtWorkMedicalCondition",
    NotAtWorkManagementIssue = "NotAtWorkManagementIssue"
}

export const classificationQuestionOptions = [
    { text: "At Work: Medical Condition", value: Classification.AtWorkMedicalCondition },
    { text: "At Work: Management Issue", value: Classification.AtWorkManagementIssue },
    { text: "Not at Work: Medical Condition", value: Classification.NotAtWorkMedicalCondition },
    { text: "Not at Work: Management Issue", value: Classification.NotAtWorkManagementIssue }
];

export enum FitnessToWork {
    FitForWork = "FitForWork",
    FitWithAdjustments = "FitWithAdjustments",
    TemporarilyUnfitForWork = "TemporarilyUnfitForWork",
    UnfitForWork = "UnfitForWork"
}

export const fitnessToWorkQuestionOptions = [
    { text: "Fit for Work", value: FitnessToWork.FitForWork },
    { text: "Fit with Adjustments", value: FitnessToWork.FitWithAdjustments },
    { text: "Temporarily Unfit for Work", value: FitnessToWork.TemporarilyUnfitForWork },
    { text: "Unfit for Work", value: FitnessToWork.UnfitForWork }
];

export enum DurationOfPhasedReturn {
    OneWeek = "OneWeek",
    TwoWeeks = "TwoWeeks",
    ThreeWeeks = "ThreeWeeks",
    FourWeeks = "FourWeeks",
    FourWeeksPlus = "FourWeeksPlus",    // V4 onwards
    SixWeeksPlus = "SixWeeksPlus"       // Legacy V2/V3
}

export const durationOfPhasedReturnQuestionOptionsLegacy = [
    { text: "1 Week", value: DurationOfPhasedReturn.OneWeek },
    { text: "2 Weeks", value: DurationOfPhasedReturn.TwoWeeks },
    { text: "3 Weeks", value: DurationOfPhasedReturn.ThreeWeeks },
    { text: "4 Weeks", value: DurationOfPhasedReturn.FourWeeks },
    { text: "4 Weeks +", value: DurationOfPhasedReturn.SixWeeksPlus } // Label changed, but value kept for backward compatibility
];

export const durationOfPhasedReturnQuestionOptions = [
    { text: "1 Week", value: DurationOfPhasedReturn.OneWeek },
    { text: "2 Weeks", value: DurationOfPhasedReturn.TwoWeeks },
    { text: "3 Weeks", value: DurationOfPhasedReturn.ThreeWeeks },
    { text: "4 Weeks", value: DurationOfPhasedReturn.FourWeeks },
    { text: "4 Weeks +", value: DurationOfPhasedReturn.FourWeeksPlus },
];

export enum WorkClassifications {
    AtWorkMedicalIssue = "AtWorkMedicalIssue",
    AtWorkManagementIssue = "AtWorkManagementIssue",
    NotAtWorkMedicalIssue = "NotAtWorkMedicalIssue",
    NotAtWorkManagementIssue = "NotAtWorkManagementIssue"
}

export const workClassificationOptions = [
    { text: "At Work - Medical issue", value: WorkClassifications.AtWorkMedicalIssue },
    { text: "At Work - Management issue", value: WorkClassifications.AtWorkManagementIssue },
    { text: "Not At Work - Medical issue", value: WorkClassifications.NotAtWorkMedicalIssue },
    { text: "Not At Work - Management issue", value: WorkClassifications.NotAtWorkManagementIssue }
];

export enum FitnessOutcome {
    FitForWork = "FitForWork",
    TemporarilyUnfitForWork = "TemporarilyUnfitForWork",
    PermanentlyUnfitForWork = "PermanentlyUnfitForWork"
}

export const fitnessOutcomeOptions = [
    { text: "Fit for Work", value: FitnessOutcome.FitForWork },
    { text: "Temporarily Unfit for Work", value: FitnessOutcome.TemporarilyUnfitForWork },
    { text: "Permanently Unfit for Work", value: FitnessOutcome.PermanentlyUnfitForWork }
];

export enum Adjustments {
    NoAdjustments = "NoAdjustments",
    ShortTerm = "ShortTerm",
    LongTerm = "LongTerm"
}

export const adjustmentOptions = [
    { text: "No adjustments likely to be required", value: Adjustments.NoAdjustments },
    { text: "Short Term / Temporary Adjustments recommended", value: Adjustments.ShortTerm },
    { text: "Long Term Adjustments recommended", value: Adjustments.LongTerm }
];

export const phasedReturnOptions = [
    { text: "1 Week", value: "1Week" },
    { text: "2 Weeks", value: "2Weeks" },
    { text: "3 Weeks", value: "3Weeks" },
    { text: "4 Weeks", value: "4Weeks" },
    { text: "5 Weeks", value: "5Weeks" },
    { text: "6 Weeks +", value: "6Weeks+" },
];

export enum FunctionalAssessmentTestType {
    Dropdown = "Dropdown",
    TextBox = "TextBox",
}

export const dropdownFunctionalAssessmentTestOptions = [
    { text: "Up to 5KG", value: "UpTo5KG" },
    { text: "10KG", value: "10KG" },
    { text: "Up to 15KG", value: "UpTo15KG" },
    { text: "Up to 23KG", value: "UpTo23KG" }
];

export const functionalAssessmentTestOptions = [
    { text: "Lift/Carry (Waist Height)", value: "FunctionAssessmentTestLiftCarryWaistHeight", type: FunctionalAssessmentTestType.Dropdown },
    { text: "Lift/Carry (Shoulder height)", value: "FunctionAssessmentTestLiftCarryShoulderHeight", type: FunctionalAssessmentTestType.Dropdown },
    { text: "Push Pull", value: "FunctionAssessmentTestPushPull", type: FunctionalAssessmentTestType.Dropdown },
    { text: "Grip strength (L, R, B)", value: "FunctionAssessmentTestGripStrengthLRB", type: FunctionalAssessmentTestType.TextBox },
    { text: "At shoulder reach: (L, R, B)", value: "FunctionAssessmentTestAtShoulderReachLRB", type: FunctionalAssessmentTestType.TextBox },
    { text: "Overhead reach: (L, R, B)", value: "FunctionAssessmentTestOverheadReachLRB", type: FunctionalAssessmentTestType.TextBox },
    { text: "Head/neck rotation (>20º): (L, R, B)", value: "FunctionAssessmentTestHeadNeckRotation20LRB", type: FunctionalAssessmentTestType.TextBox },
    { text: "Spinal range of movement", value: "FunctionAssessmentTestSpinalRangeOfMovement", type: FunctionalAssessmentTestType.TextBox },
    { text: "Kneel (L, R, B)", value: "FunctionAssessmentTestKneelLRB", type: FunctionalAssessmentTestType.TextBox },
    { text: "Squat", value: "FunctionAssessmentTestSquat", type: FunctionalAssessmentTestType.TextBox },
    { text: "Sitting", value: "FunctionAssessmentTestSitting", type: FunctionalAssessmentTestType.TextBox },
    { text: "Standing", value: "FunctionAssessmentTestStanding", type: FunctionalAssessmentTestType.TextBox },
    { text: "Walking", value: "FunctionAssessmentTestWalking", type: FunctionalAssessmentTestType.TextBox },
    { text: "Step Up e.g. stairs/step stool (L, R, B)", value: "FunctionAssessmentTestStepUpStairsStepStoolLRB", type: FunctionalAssessmentTestType.TextBox },
    { text: "Crawl", value: "FunctionAssessmentTestCrawl", type: FunctionalAssessmentTestType.TextBox },
    { text: "See", value: "FunctionAssessmentTestSee", type: FunctionalAssessmentTestType.TextBox },
    { text: "Hear", value: "FunctionAssessmentTestHear", type: FunctionalAssessmentTestType.TextBox },
    { text: "Talk", value: "FunctionAssessmentTestTalk", type: FunctionalAssessmentTestType.TextBox },
    { text: "Driving commercial machinery", value: "FunctionAssessmentTestDrivingCommercialMachinery", type: FunctionalAssessmentTestType.TextBox },
];

export enum PsychologicalAssessmentOutcome {
    NotScored = "NotScored",
    NoDepression = "NoDepression",
    MildDepression = "MildDepression",
    ModerateDepression = "ModerateDepression",
    ModeratelySevereDepression = "ModeratelySevereDepression",
    SevereDepression = "SevereDepression",
    Normal = "Normal",
    NoAnxiety = "NoAnxiety",
    MildAnxiety = "MildAnxiety",
    ModerateAnxiety = "ModerateAnxiety",
    SevereAnxiety = "SevereAnxiety",
    NoFunctionalImpairment = "NoFunctionalImpairment",
    SignificantFunctionalImpairment = "SignificantFunctionalImpairment",
    ModerateOrSevereFunctionalImpairment = "ModerateOrSevereFunctionalImpairment",
    MildToModerateFunctionalImpairment = "MildToModerateFunctionalImpairment"
}

export const psychologicalAssessmentOutcomeQuestionOptions = [
    { text: "Not scored", value: PsychologicalAssessmentOutcome.NotScored },
    { text: "No depression", value: PsychologicalAssessmentOutcome.NoDepression },
    { text: "Mild depression", value: PsychologicalAssessmentOutcome.MildDepression },
    { text: "Moderate depression", value: PsychologicalAssessmentOutcome.ModerateDepression },
    { text: "Moderately severe depression", value: PsychologicalAssessmentOutcome.ModeratelySevereDepression },
    { text: "Severe depression", value: PsychologicalAssessmentOutcome.SevereDepression },
    { text: "Normal", value: PsychologicalAssessmentOutcome.Normal },
    { text: "No Anxiety", value: PsychologicalAssessmentOutcome.NoAnxiety },
    { text: "Mild anxiety", value: PsychologicalAssessmentOutcome.MildAnxiety },
    { text: "Moderate anxiety", value: PsychologicalAssessmentOutcome.ModerateAnxiety },
    { text: "Severe anxiety", value: PsychologicalAssessmentOutcome.SevereAnxiety },
    { text: "No functional impairment", value: PsychologicalAssessmentOutcome.NoFunctionalImpairment },
    { text: "Significant functional impairment", value: PsychologicalAssessmentOutcome.SignificantFunctionalImpairment },
    { text: "Moderate to severe functional impairment", value: PsychologicalAssessmentOutcome.ModerateOrSevereFunctionalImpairment },
    { text: "Mild to moderate functional impairment", value: PsychologicalAssessmentOutcome.MildToModerateFunctionalImpairment }
];

export const getPsychologicalWellbeingGAD7OutcomeOptions = () => {
    return psychologicalAssessmentOutcomeQuestionOptions.filter(o =>
        o.value === PsychologicalAssessmentOutcome.NotScored ||
        o.value === PsychologicalAssessmentOutcome.NoAnxiety ||
        o.value === PsychologicalAssessmentOutcome.MildAnxiety ||
        o.value === PsychologicalAssessmentOutcome.ModerateAnxiety ||
        o.value === PsychologicalAssessmentOutcome.SevereAnxiety);
};

export const getPsychologicalWellbeingWSASOutcomeOptions = (readonly: boolean) => {
    if (readonly) {
        return psychologicalAssessmentOutcomeQuestionOptions.filter(o =>
            o.value === PsychologicalAssessmentOutcome.NotScored ||
            o.value === PsychologicalAssessmentOutcome.NoFunctionalImpairment ||
            o.value === PsychologicalAssessmentOutcome.SignificantFunctionalImpairment ||
            o.value === PsychologicalAssessmentOutcome.MildToModerateFunctionalImpairment ||
            o.value === PsychologicalAssessmentOutcome.ModerateOrSevereFunctionalImpairment);
    } else {
        return psychologicalAssessmentOutcomeQuestionOptions.filter(o =>
            o.value === PsychologicalAssessmentOutcome.NotScored ||
            o.value === PsychologicalAssessmentOutcome.NoFunctionalImpairment ||
            o.value === PsychologicalAssessmentOutcome.MildToModerateFunctionalImpairment ||
            o.value === PsychologicalAssessmentOutcome.ModerateOrSevereFunctionalImpairment);
    }
};

export const barriers = [
    {
        id: "PsychosocialBarriersToWorkUnhelpfulBeliefs",
        questionText: "Unhelpful Beliefs"
    },
    {
        id: "PsychosocialBarriersToWorkPoorPerceptionOfWork",
        questionText: "Poor perception of work"
    },
    {
        id: "PsychosocialBarriersToWorkLegalActionClaim",
        questionText: "Legal Action / Claim"
    },
    {
        id: "PsychosocialBarriersToWorkFinances",
        questionText: "Finances"
    },
    {
        id: "PsychosocialBarriersToWorkFamilyFactors",
        questionText: "Family Factors"
    },
    {
        id: "PsychosocialBarriersToWorkMentalEmotionalFactors",
        questionText: "Mental / Emotional Factors"
    }
];

export enum RiskAssessmentRecommendation {
    None = "None",
    ManualHandling = "ManualHandling",
    DSE = "DSE",
    StressRiskAssessment = "StressRiskAssessment",
    LoneWorking = "LoneWorking",
    NewAndExpectantMothers = "NewAndExpectantMothers",
    OtherPleaseProvideDetails = "OtherPleaseProvideDetails"
}

export const riskAssessmentRecommendationQuestionOptions = [
    { text: "None", value: RiskAssessmentRecommendation.None },
    { text: "Manual Handling", value: RiskAssessmentRecommendation.ManualHandling },
    { text: "DSE", value: RiskAssessmentRecommendation.DSE },
    { text: "Stress Risk Assessment", value: RiskAssessmentRecommendation.StressRiskAssessment },
    { text: "Lone Working", value: RiskAssessmentRecommendation.LoneWorking },
    { text: "New and Expectant Mothers", value: RiskAssessmentRecommendation.NewAndExpectantMothers },
    { text: "Other - Please provide details", value: RiskAssessmentRecommendation.OtherPleaseProvideDetails },
];

export enum DurationOfMedicalCondition {
    LessThanTwelveMonthsAndUnlikelyToLastLonger = "LessThanTwelveMonthsAndUnlikelyToLastLonger",
    LessThanTwelveMonthsButLikelyToLastAtLeastTwelveMonths = "LessThanTwelveMonthsButLikelyToLastAtLeastTwelveMonths",
    TwelveMonthsOrLonger = "TwelveMonthsOrLonger"
}

export const durationOfMedicalConditionQuestionOptions = [
    { text: "Less than 12 months and unlikely to last longer", value: DurationOfMedicalCondition.LessThanTwelveMonthsAndUnlikelyToLastLonger },
    {
        text: "Less than 12 months but likely to last at least 12 months",
        value: DurationOfMedicalCondition.LessThanTwelveMonthsButLikelyToLastAtLeastTwelveMonths
    },
    { text: "12 months or longer", value: DurationOfMedicalCondition.TwelveMonthsOrLonger }
];

export enum ApplyEqualityAct {
    Yes = "Yes",
    No = "No",
    NotAnswered = "NotAnswered"
}

export const applyEqualityActQuestionOptions = [
    { text: "Yes", value: ApplyEqualityAct.Yes },
    { text: "No", value: ApplyEqualityAct.No },
    { text: "Not Answered", value: ApplyEqualityAct.NotAnswered }
];

export enum DetailsOfOnwardReferral {
    None = "None",
    Physiotherapy = "Physiotherapy",
    Counselling = "Counselling",
    SelfHelpSignPosting = "SelfHelpSignPosting",
    CBT = "CBT",
    OtherDetailsBelow = "OtherDetailsBelow"
}

export const detailsOfOnwardReferralQuestionOptions = [
    { text: "None", value: DetailsOfOnwardReferral.None },
    { text: "Physiotherapy", value: DetailsOfOnwardReferral.Physiotherapy },
    { text: "Counselling", value: DetailsOfOnwardReferral.Counselling },
    { text: "Self Help / Sign Posting", value: DetailsOfOnwardReferral.SelfHelpSignPosting },
    { text: "CBT ", value: DetailsOfOnwardReferral.CBT },
    { text: "Other - details below", value: DetailsOfOnwardReferral.OtherDetailsBelow },
];

export enum DetailsOfFurtherAppointments {
    NoReview = "NoReview",
    OHAReview = "OHAReview",
    PhysiotherapyReview = "PhysiotherapyReview",
    MentalHealthPractitionerReview = "MentalHealthPractitionerReview",
    OHPhysicianReview = "OHPhysicianReview",
    ReferToGPSpecialist = "ReferToGPSpecialist"
}

export const detailsOfFurtherAppointmentsQuestionOptions = [
    { text: "No Review", value: DetailsOfFurtherAppointments.NoReview },
    { text: "OHA Review", value: DetailsOfFurtherAppointments.OHAReview },
    { text: "Physiotherapy Review", value: DetailsOfFurtherAppointments.PhysiotherapyReview },
    { text: "Mental Health Practitioner Review", value: DetailsOfFurtherAppointments.MentalHealthPractitionerReview },
    { text: "OH Physician Review", value: DetailsOfFurtherAppointments.OHPhysicianReview },
    { text: "Refer to GP / Specialist", value: DetailsOfFurtherAppointments.ReferToGPSpecialist },
];

export const adjustmentsRecommendedOptions = {
    ModifyInstructionsManuals: "Modify instructions and manuals",
    ModifyProcedures: "Modify procedures for testing, assessment or appraisal",
    ModifyWorkPatternsManagementSystems: "Modify work patterns or management systems",
    PotentiallyPermanentlyUnfitForDuties: "Potentially Permanently Unfit for Contracted Duties - OHP Review Pending",
    ProvideMentor: "Provide a mentor or ‘buddy’ while they regain confidence in the workplace",
    ProvideAlternativeWork: "Provide alternative work",
    ProvideNewModifiedEquipment: "Provide New or modified equipment or tools, including IT eqt",
    ProvideSupervision: "Provide supervision",
    ReallocateWorkWithinTeam: "Reallocate work within the team"
};

export enum SectionNames {
    ConsultationDetails = "ConsultationDetails"
}

const validateAdjustmentsEndDate = (value: moment.Moment) => {
    return value && value.isBetween(moment().startOf("day"), moment().add(12, "weeks"));
};

const isDateInPast = (value: moment.Moment) => {
    return value && value.isBefore(moment().startOf("day"));
};

export const notInThePastValidator = createValidator((value: moment.Moment) => !isDateInPast(value), "Must not be in the past");

export const dateInThePastWarning = createValidator(
    (value: moment.Moment) => !isDateInPast(value), "You have entered a date in the past, please check this is correct");

export const notMoreThan12WeeksInTheFuture = createValidator(
    (value: moment.Moment) => validateAdjustmentsEndDate(value),
    "Cannot be more than 12 weeks in the future");

export interface ReferralReportDetails extends ReportDetails {
    id: string;
    data: QuestionnaireData;
    eTag: string;
    isDefaultCustomerType?: boolean;
    customerType?: string;
}

export enum Level {
    AdviceProvidedOnly = "AdviceProvidedOnly",
    ClinicalAssessmentNeeded = "ClinicalAssessmentNeeded",
    PathologyKnown = "PathologyKnown",
    LongTermRestriction = "LongTermRestriction"
}

export const levelOptions = [
    { text: "1: Advice Provided Only", value: Level.AdviceProvidedOnly },
    { text: "2: Clinical Assessment Needed", value: Level.ClinicalAssessmentNeeded },
    { text: "3: Pathology known (Diagnosis and escalation of care)", value: Level.PathologyKnown },
    { text: "4: Long term restriction", value: Level.LongTermRestriction }
];

export enum CurrentStatus {
    Absent = "Absent",
    Restricted = "Restricted",
    WorkingNormallyPPOandSPO = "WorkingNormallyPPOandSPO",
    ModifiedWorkingNormallyPPOorSPO = "ModifiedWorkingNormallyPPOorSPO",
    RampUp = "RampUp"
}

export const currentStatusOptions = [
    { text: "Absent", value: CurrentStatus.Absent },
    { text: "Restricted", value: CurrentStatus.Restricted },
    { text: "Working Normally (PPO and SPO)", value: CurrentStatus.WorkingNormallyPPOandSPO },
    { text: "Modified Working Normally (PPO or SPO)", value: CurrentStatus.ModifiedWorkingNormallyPPOorSPO },
    { text: "Ramp Up", value: CurrentStatus.RampUp }
];

export const injuredQuestionOptions = [
    { text: "Non-Work Concern", value: "Non-WorkConcern" },
    { text: "Work Aggravated Concern", value: "WorkAggravatedConcern" },
    { text: "Work Concern", value: "WorkConcern" }
];

enum CovidSubcategory {
    LongCovid = "LongCovid",
    Other = "Other"
}

export const medicalClassificationSubCategoryOptions: {
    [key: string]: { value: string; text: string; old?: boolean }[];
} = {
    AssaultAtWork: [
        { value: "Ankle", text: "Ankle" },
        { value: "Back", text: "Back", old: true },
        { value: "Elbow", text: "Elbow" },
        { value: "Face", text: "Face" },
        { value: "Foot", text: "Foot" },
        { value: "Hand", text: "Hand" },
        { value: "Head", text: "Head" },
        { value: "Hip", text: "Hip" },
        { value: "Knee", text: "Knee" },
        { value: "LowerBack", text: "Low Back" },
        { value: "LowerLimb", text: "Lower Limb" },
        { value: "Multiple", text: "Multiple" },
        { value: "Neck", text: "Neck" },
        { value: "Pelvis", text: "Pelvis" },
        { value: "Shoulders", text: "Shoulder" },
        { value: "UpperArm", text: "Upper arm", old: true },
        { value: "UpperBack", text: "Upper Back" },
        { value: "UpperLimb", text: "Upper Limb" },
        { value: "WholeBody", text: "Whole Body" },
        { value: "Wrist", text: "Wrist" },
    ],
    Autoimmune: [
        { value: "Fibryomyalgia", text: "Fibryomyalgia" },
        { value: "MultipleSclerosis", text: "Multiple Sclerosis" },
        { value: "RheumatoidArthritis", text: "Rheumatoid Arthritis" },
        { value: "SystemicLupusErythematosus", text: "Systemic Lupus Erythematosus" },
        { value: "Other", text: "Other" },
    ],
    Cancer: [
        { value: "Bladder", text: "Bladder" },
        { value: "Bowel", text: "Bowel" },
        { value: "Breast", text: "Breast" },
        { value: "Cervical", text: "Cervical" },
        { value: "Lung", text: "Lung" },
        { value: "Ovarian", text: "Ovarian" },
        { value: "Prostate", text: "Prostate" },
        { value: "Skin", text: "Skin" },
        { value: "Other", text: "Other" },
    ],
    Cardiovascular: [
        { value: "Arrythmias", text: "Arrythmias" },
        { value: "CardioMyopathy", text: "Cardio Myopathy" },
        { value: "CongenitalHeartDefects", text: "Congenital heart defects" },
        { value: "CoronaryHeartDiseaseMIVascular", text: "Coronary Heart disease, MI, Vascular" },
        { value: "Other", text: "Other" }
    ],
    EarNoseAndThroat: [
        { value: "Dizziness", text: "Dizziness" },
        { value: "Hearing", text: "Hearing Loss" },
        { value: "Infection", text: "Infection" },
        { value: "Sinus problems", text: "Sinus problems" },
        { value: "Tinnitus", text: "Tinnitus" },
        { value: "Other", text: "Other" },
    ],
    Endocrine: [
        { value: "Diabetes", text: "Diabetes" },
        { value: "Thyroid", text: "Thyroid" },
        { value: "Other", text: "Other" }
    ],
    Gastrointestinal: [
        { value: "Gallstones", text: "Gallstones" },
        { value: "InflammatoryBowelDisease", text: "Inflammatory Bowel Disease" },
        { value: "Other", text: "Other" }
    ],
    MSDProblem: [
        { value: "Ankle", text: "Ankle" },
        { value: "Back", text: "Back", old: true },
        { value: "Elbow", text: "Elbow" },
        { value: "Foot", text: "Foot" },
        { value: "Hand", text: "Hand" },
        { value: "Head", text: "Head" },
        { value: "Hip", text: "Hip" },
        { value: "Knee", text: "Knee" },
        { value: "LowerBack", text: "Low Back" },
        { value: "LowerLimb", text: "Lower Limb" },
        { value: "Multiple", text: "Multiple" },
        { value: "Neck", text: "Neck" },
        { value: "Pelvis", text: "Pelvis" },
        { value: "Shoulders", text: "Shoulder" },
        { value: "UpperArm", text: "Upper arm", old: true },
        { value: "UpperBack", text: "Upper Back" },
        { value: "UpperLimb", text: "Upper Limb" },
        { value: "WholeBody", text: "Whole Body" },
        { value: "Wrist", text: "Wrist" }
    ],
    Neurological: [
        { value: "Alzheimers", text: "Alzheimers" },
        { value: "Epilepsy", text: "Epilepsy" },
        { value: "MS", text: "MS" },
        { value: "ParkinsonsDisease", text: "Parkinson's Disease" },
        { value: "Stroke", text: "Stroke" },
        { value: "Other", text: "Other" },
    ],
    Ophthalmic: [
        { value: "Catatract", text: "Cataract" },
        { value: "Other", text: "Other" }
    ],
    Respiratory: [
        { value: "Asthma", text: "Asthma" },
        { value: "COPD", text: "COPD" },
        { value: "CysticFibrosis", text: "Cystic Fibrosis" },
        { value: "Other", text: "Other" },
    ],
    SkinDisorders: [
        { value: "Cellulitis", text: "Cellulitis" },
        { value: "Eczema", text: "Eczema" },
        { value: "HandDermatitis", text: "Hand Dermatitis" },
        { value: "Psoriasis", text: "Psoriasis" },
        { value: "Other", text: "Other" },
    ],
    SpectrumDisorders: [
        { value: "ADHD", text: "ADHD" },
        { value: "Autism", text: "Autism" },
        { value: "Dyslexia", text: "Dyslexia" },
        { value: "Other", text: "Other" },
    ],
    Stress: [
        { value: "Bullying", text: "Bullying" },
        { value: "Disciplinary", text: "Disciplinary" },
        { value: "Domestic", text: "Domestic" },
        { value: "Financial", text: "Financial" },
        { value: "Management", text: "Management" },
        { value: "OrganisationalChange", text: "Organisational Change" },
        { value: "PersonalTraumaOrIncident", text: "Personal Trauma or Incident" },
        { value: "Relationships", text: "Relationships" },
        { value: "TraumaticIncidentAtWork", text: "Traumatic Incident at Work" },
        { value: "Verbal Assault", text: "Verbal Assault" },
        { value: "WorkAndPersonal", text: "Work and Personal" },
        { value: "Workload", text: "Workload" },
        { value: "Other", text: "Other" },
        { value: "NonWorkDomestic", text: "Non Work: Domestic", old: true },
        { value: "NonWorkFinancial", text: "Non Work: Financial", old: true },
        { value: "NonWorkOther", text: "Non Work: Other", old: true },
        { value: "NonWorkPersonalTraumaOrIncident", text: "Non Work: Personal Trauma or Incident", old: true },
        { value: "WorkBullying", text: "Work: Bullying", old: true },
        { value: "WorkDisciplinary", text: "Work: Disciplinary", old: true },
        { value: "WorkManagement", text: "Work: Management", old: true },
        { value: "WorkOrganisationalChange", text: "Work: Organisational Change", old: true },
        { value: "WorkOther", text: "Work: Other", old: true },
        { value: "WorkRelationships", text: "Work: Relationships", old: true },
        { value: "WorkTraumaticIncidentAtWork", text: "Work: Traumatic Incident at Work", old: true },
        { value: "WorkWorkload", text: "Work: Workload", old: true },
    ],
    SubstanceMisuse: [
        { value: "Alcohol", text: "Alcohol" },
        { value: "Drugs", text: "Drugs" }
    ],
    "COVID-19": [
        { value: CovidSubcategory.LongCovid, text: "Long Covid" },
        { value: CovidSubcategory.Other, text: "Other" }
    ]
};

export const ohaMedicalClassificationOptions = [
    { text: "Anxiety", value: "Anxiety" },
    { text: "Assault at Work - MSD", value: "AssaultAtWork" },
    { text: "Autoimmune Disorder", value: "Autoimmune" },
    { text: "Bereavement", value: "Bereavement" },
    { text: "Cancer", value: "Cancer" },
    { text: "Cardiovascular", value: "Cardiovascular" },
    { text: "COVID-19", value: "COVID-19" },
    { text: "Depression", value: "Depression" },
    { text: "Ear Nose and Throat - ENT", value: "EarNoseAndThroat" },
    { text: "Endocrine", value: "Endocrine" },
    { text: "Gastrointestinal", value: "Gastrointestinal" },
    { text: "Genitourinary", value: "Genitourinary" },
    { text: "Gynaecological ", value: "Gynaecological " },
    { text: "Headache/Migraine", value: "HeadacheMigraine" },
    { text: "Infection", value: "Infection" },
    { text: "Injury/Fracture", value: "InjuryFracture" },
    { text: "Liver Disease", value: "LiverDisease" },
    { text: "Maternity Related Concerns/Pregnancy", value: "MaternityPregnancy" },
    { text: "Medical Condition", value: "MedicalCondition", old: true },
    { text: "Mental Illness", value: "MentalIllness" },
    { text: "Menopause", value: "Menopause" },
    { text: "MSD Problem", value: "MSDProblem" },
    { text: "Nervous System Disorders", value: "NervousSystemDisorders" },
    { text: "Neurological", value: "Neurological" },
    { text: "No Issues", value: "NoIssues", old: true },
    { text: "Ophthalmic", value: "Ophthalmic" },
    { text: "Respiratory Conditions", value: "Respiratory" },
    { text: "Short Term Sickness", value: "ShortTermSickness" },
    { text: "Skin Disorders", value: "SkinDisorders" },
    { text: "Spectrum Disorders", value: "SpectrumDisorders" },
    { text: "Stress", value: "Stress" },
    { text: "Substance Misuse", value: "SubstanceMisuse" },
    { text: "Surgical Procedures", value: "SurgicalProcedures" },
    { text: "Verbal Assault – Stress", value: "VerbalAssaultStress", old: true }
];

export enum OriginTypes {
    MSS = "MSS",
    Other = "Other",
    Illness = "Illness",
    Accident = "Accident",
    Surgery = "Surgery"
}

export const originTypeOptions = [
    { text: "Other", value: OriginTypes.Other },
    { text: "MSS", value: OriginTypes.MSS },
    { text: "Illness", value: OriginTypes.Illness },
    { text: "Accident", value: OriginTypes.Accident },
    { text: "Surgery", value: OriginTypes.Surgery }
];

export enum Symptoms {
    Pain = "Pain",
    Ache = "Ache",
    Impact = "Impact",
    Psychological = "Psychological",
    Fracture = "Fracture",
    EyeInjury = "EyeInjury",
    Fatigue = "Fatigue",
    Bruising = "Bruising",
    Numbness = "Numbness",
    Abrasion = "Abrasion",
    Asymptomatic = "Asymptomatic",
    Personal = "Personal",
    Laceration = "Laceration",
    SprainStrain = "SprainStrain",
    Nausea = "Nausea",
    PinsAndNeedles = "PinsAndNeedles",
    Burn = "Burn",
    Itching = "Itching",
    Headaches = "Headaches",
    PunctureWound = "PunctureWound",
    ReducedRangeOfMovement = "ReducedRangeOfMovement",
    PoppingOrClicking = "PoppingOrClicking",
    Crush = "Crush",
    ReducedGripStrength = "ReducedGripStrength"
}

export const symptomsOptions = [
    { text: "Pain", value: Symptoms.Pain },
    { text: "Ache", value: Symptoms.Ache },
    { text: "Impact", value: Symptoms.Impact },
    { text: "Psychological", value: Symptoms.Psychological },
    { text: "Fracture ", value: Symptoms.Fracture },
    { text: "Eye Injury", value: Symptoms.EyeInjury },
    { text: "Fatigue ", value: Symptoms.Fatigue },
    { text: "Bruising ", value: Symptoms.Bruising },
    { text: "Numbness ", value: Symptoms.Numbness },
    { text: "Abrasion ", value: Symptoms.Abrasion },
    { text: "Asymptomatic ", value: Symptoms.Asymptomatic },
    { text: "Personal ", value: Symptoms.Personal },
    { text: "Laceration ", value: Symptoms.Laceration },
    { text: "Sprain / Strain ", value: Symptoms.SprainStrain },
    { text: "Nausea ", value: Symptoms.Nausea },
    { text: "Pins And Needles ", value: Symptoms.PinsAndNeedles },
    { text: "Burn ", value: Symptoms.Burn },
    { text: "Itching ", value: Symptoms.Itching },
    { text: "Headaches ", value: Symptoms.Headaches },
    { text: "Puncture Wound ", value: Symptoms.PunctureWound },
    { text: "Reduced Range Of Movement ", value: Symptoms.ReducedRangeOfMovement },
    { text: "Popping / Clicking ", value: Symptoms.PoppingOrClicking },
    { text: "Crush ", value: Symptoms.Crush },
    { text: "Reduced Grip Strength ", value: Symptoms.ReducedGripStrength }
];

export enum ToyotaClinicalNotesLevel {
    Level1Discharged = "Level1Discharged",
    Level2 = "Level2",
    Level2Discharged = "Level2Discharged",
    Level3 = "Level3",
    Level3Discharged = "Level3Discharged",
    Level4 = "Level4"
}

export const toyotaClinicalNotesLevelOptions = [
    { text: "LEVEL 1 - DISCHARGED", value: ToyotaClinicalNotesLevel.Level1Discharged },
    { text: "LEVEL 2", value: ToyotaClinicalNotesLevel.Level2 },
    { text: "LEVEL 2 - DISCHARGED", value: ToyotaClinicalNotesLevel.Level2Discharged },
    { text: "LEVEL 3", value: ToyotaClinicalNotesLevel.Level3 },
    { text: "LEVEL 3 - DISCHARGED", value: ToyotaClinicalNotesLevel.Level3Discharged },
    { text: "LEVEL 4", value: ToyotaClinicalNotesLevel.Level4 }
];
